import { z } from 'zod';
import { TAG_KINDS, TAG_SCOPES } from '../constants';
import { idSchema, updatedAtSchema } from './common';

/**
 * No kind means volunteers: a coordinator page loaded before a deploy that
 * added post labels keeps listing and creating its tags until it is reloaded.
 * The app itself always names the kind.
 */
const tagKindSchema = z.enum(TAG_KINDS).default('volunteer');

const fields = {
  label: z.string().trim().min(1).max(100),
  /** CSS colour token, e.g. an oklch() value from the tag palette. */
  colour: z.string().trim().max(60),
  scope: z.enum(TAG_SCOPES),
};

export const tagInputSchema = z.object({
  ...fields,
  scope: fields.scope.default('shared'),
  /** Volunteers or posts — fixed at creation. */
  kind: tagKindSchema,
});

/**
 * Built from the defaults-free field shapes, like `externalGroupUpdateSchema`:
 * `.partial()` would keep `scope`'s default, so renaming a private tag would
 * parse to `scope: 'shared'` and publish it to every coordinator. `kind` is not
 * editable at all — the tag's links would end up on the wrong entity.
 */
export const tagUpdateSchema = z
  .object(fields)
  .partial()
  .extend({ updatedAt: updatedAtSchema })
  // `updatedAt` alone would reach the database as an empty `set` and throw.
  .refine((v) => Object.keys(v).length > 1, { message: 'ni sprememb' });

export const tagListQuerySchema = z.object({ kind: tagKindSchema });

export const tagSchema = tagInputSchema.extend({
  id: idSchema,
  /** Set for private tags; enforced server-side (SPEC §2 auth). */
  ownerCoordinatorId: idSchema.nullable(),
  updatedAt: updatedAtSchema,
});

export type TagInput = z.infer<typeof tagInputSchema>;
export type Tag = z.infer<typeof tagSchema>;
